import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import {
  SafetyCertificateOutlined,
  FileTextOutlined,
  TeamOutlined,
  ReadOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import '../styles/home.css';

const PAIN_POINTS = [
  {
    label: 'Tồn kho & vốn',
    desc: 'Vốn chết trên kệ vì nhập hàng theo cảm tính, không theo vòng quay thực tế.',
  },
  {
    label: 'Nhân sự dược sĩ',
    desc: 'Nhân viên giỏi nghỉ việc, kinh nghiệm tư vấn tại quầy không được chuẩn hóa để bàn giao lại.',
  },
  {
    label: 'Tái thẩm định GPP',
    desc: 'Quy định thay đổi liên tục, hồ sơ và quy trình dễ bị động mỗi khi đoàn kiểm tra đến.',
  },
  {
    label: 'Cạnh tranh chuỗi lớn',
    desc: 'Khách quen dần chuyển sang chuỗi vì trải nghiệm mua hàng và mức giá khó cạnh tranh lại.',
  },
];

const COURSE_TOPICS = [
  'Sale – Bán Hàng & Tư Vấn Tại Quầy',
  'Quản Trị Nhân Sự Nhà Thuốc',
  'Vận Hành & Tài Chính Nhà Thuốc',
  'Marketing Nhà Thuốc (Trade – Online – Xây Kênh)',
  'Pháp Lý, Thuế & Thủ Tục Nhà Thuốc',
  'Chuyên Môn Dược & Tra Cứu Lâm Sàng',
  'Ứng Dụng Công Nghệ & AI Trong Nhà Thuốc',
];

const TOOLS = [
  { title: 'Quy Trình Vận Hành S.O.P', desc: '26 quy trình chuẩn hoá, tải file Word chỉnh sửa ngay' },
  { title: 'Đo Lường Sức Khỏe Nhà Thuốc', desc: 'Chẩn đoán tài chính, tồn kho, điểm nghẽn vận hành' },
  { title: 'Trắc Nghiệm Kiến Thức, Kỹ Năng', desc: '240 câu hỏi tình huống, xếp loại năng lực đứng quầy' },
  { title: 'Cơ Chế Lương Thưởng Quầy Thuốc', desc: 'Xây thang bảng lương 2P/3P & quỹ thưởng lãi gộp' },
  { title: 'Xây Chương Trình Bán Hàng', desc: 'Phễu sự kiện 2 tầng, mục tiêu x2 doanh số' },
];

const REASONS = [
  {
    title: 'Biên soạn từ vận hành thật',
    desc: 'Nội dung đúc kết từ kinh nghiệm quản trị nhà thuốc thực tế của Thế Thảo Pharma, không phải lý thuyết hàn lâm sao chép.',
  },
  {
    title: 'Học xong dùng được ngay',
    desc: 'Đi kèm 88 file Excel / Word / PDF dùng trực tiếp cho quầy của bạn — không cần tự soạn lại từ đầu.',
  },
  {
    title: 'Không học một mình',
    desc: 'Cộng đồng Nhà Thuốc & Dược Sĩ 5.0 đồng hành hỏi đáp, cùng tháo gỡ tình huống thực tế mỗi ngày.',
  },
];

export function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="home-page">
      {/* HERO */}
      <section className="home-hero">
        <div className="home-hero-copy">
          <p className="home-hero-kicker">Đào tạo nhà thuốc thực chiến · Dành cho chủ nhà thuốc & quầy thuốc</p>
          <h1 className="home-hero-title">
            Nhà thuốc không thua vì thiếu thuốc.
            <br />
            Nhà thuốc thua vì thiếu bài bản.
          </h1>
          <p className="home-hero-desc">
            Thế Thảo Pharma đóng gói kinh nghiệm vận hành nhà thuốc thực chiến thành hệ thống đào
            tạo có cấu trúc — quản trị, bán hàng, tài chính, pháp lý GPP — để bạn học và áp dụng
            ngay trong tuần này.
          </p>
          <div className="home-hero-actions">
            <Button type="primary" size="large" onClick={() => navigate('/courses')}>
              Xem chương trình đào tạo
            </Button>
            <button className="home-hero-secondary" onClick={() => navigate('/tools')}>
              Khám phá bộ công cụ miễn phí
            </button>
          </div>
        </div>

        <div className="home-hero-media">
          <img
            src="/images/quay-thuoc-chuan-gpp.jpg"
            alt="Dược sĩ tư vấn khách hàng tại quầy thuốc chuẩn GPP"
          />
          <div className="home-hero-stat">
            <strong>32 Module · 88 Tài liệu</strong>
            <span>thực chiến, dùng được ngay</span>
          </div>
        </div>
      </section>

      {/* VẤN ĐỀ THỰC TẾ */}
      <section className="home-section home-section--dark">
        <div className="home-section-inner">
          <h2 className="home-section-title">
            Vận hành giỏi một nhà thuốc khó hơn nhiều so với bán đúng một viên thuốc.
          </h2>
          <div className="home-pain-grid">
            {PAIN_POINTS.map((p) => (
              <div className="home-pain-item" key={p.label}>
                <div className="home-pain-label">{p.label}</div>
                <div className="home-pain-desc">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HAI TRỤ CỘT: HỌC TẬP + CÔNG CỤ */}
      <section className="home-section">
        <div className="home-section-inner">
          <h2 className="home-section-title home-section-title--light home-pillars-title">
            Nền tảng đứng trên hai trụ cột.
          </h2>

          <div className="home-pillars">
            <div className="home-pillar">
              <div className="home-pillar-head">
                <ReadOutlined />
                <span>Khóa Học Đào Tạo</span>
              </div>
              <p className="home-pillar-stat">7 chuyên đề · 32 module · 3 lộ trình đóng gói</p>
              <p className="home-lead">
                Kiến thức được đóng gói có cấu trúc theo lộ trình, thay vì một danh sách bài giảng
                rời rạc.
              </p>
              <ul className="home-pillar-list">
                {COURSE_TOPICS.map((title) => (
                  <li key={title}>{title}</li>
                ))}
              </ul>
              <Button type="default" size="large" onClick={() => navigate('/courses')}>
                Xem chương trình đào tạo
              </Button>
            </div>

            <div className="home-pillar-divider" aria-hidden="true" />

            <div className="home-pillar">
              <div className="home-pillar-head">
                <ApartmentOutlined />
                <span>Công Cụ Thực Chiến</span>
              </div>
              <p className="home-pillar-stat">5 công cụ đang hoạt động, dùng ngay không cần chờ khóa học</p>
              <p className="home-lead">
                Nhập số liệu quầy của bạn, nhận kết quả chẩn đoán và file xuất dùng luôn trong vài
                phút.
              </p>
              <ul className="home-pillar-list home-pillar-list--tools">
                {TOOLS.map((tool) => (
                  <li key={tool.title}>
                    <strong>{tool.title}</strong>
                    <span>{tool.desc}</span>
                  </li>
                ))}
              </ul>
              <Button type="default" size="large" onClick={() => navigate('/tools')}>
                Khám phá công cụ thực chiến
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* VÌ SAO CHỌN THẾ THẢO PHARMA */}
      <section className="home-section home-section--muted">
        <div className="home-section-inner">
          <h2 className="home-section-title home-section-title--light">
            Vì sao chọn Thế Thảo Pharma
          </h2>
          <div className="home-reason-grid">
            {REASONS.map((r, idx) => (
              <div className="home-reason-item" key={r.title}>
                <div className="home-reason-icon">
                  {idx === 0 && <SafetyCertificateOutlined />}
                  {idx === 1 && <FileTextOutlined />}
                  {idx === 2 && <TeamOutlined />}
                </div>
                <h3 className="home-reason-title">{r.title}</h3>
                <p className="home-reason-desc">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CỘNG ĐỒNG */}
      <section className="home-section">
        <div className="home-section-inner home-community">
          <div>
            <h2 className="home-section-title home-section-title--light">
              Cộng Đồng Nhà Thuốc & Dược Sĩ 5.0
            </h2>
            <p className="home-lead" style={{ marginBottom: 0 }}>
              Nơi các chủ quầy thuốc trao đổi kinh nghiệm quản trị, vận hành thực chiến và nhận
              giải đáp chuyên môn từ đội ngũ Dược sĩ Thế Thảo Pharma.
            </p>
          </div>
          <a
            href="https://www.facebook.com/groups/934460299578977"
            target="_blank"
            rel="noopener noreferrer"
            className="home-community-link"
          >
            <TeamOutlined /> Tham gia cộng đồng
          </a>
        </div>
      </section>

      {/* CTA CUỐI TRANG */}
      <section className="home-cta">
        <div className="home-section-inner home-cta-inner">
          <div>
            <h2 className="home-cta-title">Sẵn sàng đưa nhà thuốc vào bài bản?</h2>
            <p className="home-cta-desc">
              Hotline / Zalo tư vấn: <strong>0812.539.539</strong> · 60B Nguyễn Huy Tưởng, Thanh
              Xuân, Hà Nội
            </p>
          </div>
          <div className="home-cta-actions">
            <Button size="large" className="home-cta-btn-primary" onClick={() => navigate('/register')}>
              Đăng ký học ngay
            </Button>
            <button className="home-cta-btn-ghost" onClick={() => navigate('/courses')}>
              <ReadOutlined /> Xem chương trình
            </button>
            <button className="home-cta-btn-ghost" onClick={() => navigate('/tools')}>
              <ApartmentOutlined /> Công cụ thực chiến
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
